import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'lg'

interface BaseButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
}

interface ButtonAsButtonProps extends BaseButtonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  as?: 'button'
  children: ReactNode
}

interface ButtonAsLinkProps extends BaseButtonProps {
  as: typeof Link
  to: string
  children: ReactNode
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps

const buttonVariants = {
  primary:  'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500',
  secondary:'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500',
  outline:  'bg-white text-green-600 border border-green-600 hover:bg-green-50 focus:ring-green-500',
  ghost:    'text-green-600 hover:bg-green-50 focus:ring-green-500',
}


const buttonSizes = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, children, ...props }, ref) => {
    const baseClasses = clsx(
      'inline-flex items-center justify-center font-medium rounded-lg',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'transition-colors duration-200',
      buttonVariants[variant],
      buttonSizes[size],
      className
    )

    if ('as' in props && props.as === Link) {
      const { as: Component, ...linkProps } = props
      return (
        <Component
          ref={ref as any}
          className={baseClasses}
          {...linkProps}
        >
          {children}
        </Component>
      )
    }

    const { as, ...buttonProps } = props as ButtonAsButtonProps
    return (
      <button
        ref={ref as any}
        className={baseClasses}
        {...buttonProps}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'